import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ImageIcon } from 'lucide-react';
import api from '@/lib/api';
import MusicCardCompact from '@/components/MusicCardCompact';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

export default function Unidentified() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState(null);
  const [fixTitle, setFixTitle] = useState('');
  const [fixArtist, setFixArtist] = useState('');
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/cards', { params: { status: 'unidentified' } });
      setCards(data.cards || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const openFix = (card) => {
    setFixing(card);
    setFixTitle(card.title || '');
    setFixArtist(card.artist || '');
  };

  const submitFix = async (e) => {
    e.preventDefault();
    if (!fixing) return;
    if (!fixTitle.trim() || !fixArtist.trim()) {
      toast.error('Title and artist required');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/cards/${fixing.id}`, {
        title: fixTitle.trim(),
        artist: fixArtist.trim(),
      });
      if (data?.status === 'active') {
        toast.success(`Fixed: ${data.artist} - ${data.title}`);
      } else {
        toast.message('Saved but no match found yet');
      }
      setFixing(null);
      fetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (card) => {
    if (!window.confirm('Delete this card?')) return;
    try {
      await api.delete(`/cards/${card.id}`);
      toast.success('Deleted');
      fetch();
    } catch (e) {
      toast.error('Failed');
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-5">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            To fix
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Pulls our AI couldn&rsquo;t identify. Tell us what they are and we&rsquo;ll re-enrich them.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={`skeleton-${i}`} className="h-20 rounded-xl" />
          ))}
        </div>
      ) : cards.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="unidentified-empty">
          <AlertTriangle className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
          <h3 className="font-display text-lg font-semibold">All identified</h3>
          <p className="text-sm text-muted-foreground">No low-confidence pulls right now.</p>
        </div>
      ) : (
        <ul className="space-y-2 max-w-4xl mx-auto" data-testid="unidentified-cards-list">
          {cards.map((card) => (
            <li key={card.id}>
              <MusicCardCompact
                card={card}
                onOpen={openFix}
                onDelete={handleDelete}
                onFixUnidentified={openFix}
                showUnidentifiedActions
              />
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!fixing} onOpenChange={(o) => !o && setFixing(null)}>
        <DialogContent className="max-w-md bg-card border-border" data-testid="unidentified-fix-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Fix this pull</DialogTitle>
            <DialogDescription>
              We&rsquo;ll re-search iTunes + MusicBrainz with your corrections.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mb-2">
            <div className="w-20 h-20 rounded-lg bg-secondary overflow-hidden grid place-items-center shrink-0">
              {fixing?.original_image_b64 ? (
                <img
                  src={`data:image/jpeg;base64,${fixing.original_image_b64}`}
                  alt="Screenshot"
                  className="w-full h-full object-cover"
                />
              ) : (
                <ImageIcon className="w-6 h-6 text-muted-foreground" />
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {fixing?.reason || 'Provide the correct artist and title.'}
            </div>
          </div>
          <form onSubmit={submitFix} className="space-y-3">
            <div>
              <Label htmlFor="fix-artist">Artist</Label>
              <Input
                id="fix-artist"
                value={fixArtist}
                onChange={(e) => setFixArtist(e.target.value)}
                placeholder="e.g. Daft Punk"
                data-testid="fix-artist-input"
                required
              />
            </div>
            <div>
              <Label htmlFor="fix-title">Title</Label>
              <Input
                id="fix-title"
                value={fixTitle}
                onChange={(e) => setFixTitle(e.target.value)}
                placeholder="e.g. Get Lucky"
                data-testid="fix-title-input"
                required
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="ghost" onClick={() => setFixing(null)} className="rounded-lg">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="rounded-lg" data-testid="fix-submit-button">
                {saving ? 'Re-enriching…' : 'Save & re-enrich'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
